import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../store/userStore";
import toast from "react-hot-toast";
import {
  deleteStorageItem,
  getStorageItem,
  setStorageItem,
} from "../../utils/sessionStorage";
import {
  User,
  Mail,
  Phone,
  ShieldCheck,
  Edit3,
  X,
  Save,
  Camera,
  MapPin,
} from "lucide-react";
import { API } from "../../utils/axios";

const MyProfile = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useUserStore();
  const [userData, setUserData] = useState(user);
  const [loading, setLoading] = useState(false)
  const updateUserDetails = useUserStore((state) => state.fullUpdate);

  const updateUserProfile = async () => {
    if (!user?.id) {
      toast.error("Please Login First");
      return;
    }

    setLoading(true)

    await API.patch(`/user/${user?.id}`, {
      firstName: userData?.firstName,
      lastName: userData?.lastName,
      email: userData?.email,
      phone: userData?.phone,
    })
      .then((res) => {
        setIsEditing(false);
        toast.success("Profile Updated");
        setStorageItem("firstName", res.data.data?.firstName);
        setStorageItem("lastName", res.data.data?.lastName);
        setStorageItem("email", res.data.data?.email);
        setStorageItem("phoneNo", res.data.data?.phone);
        setStorageItem("user-data", JSON.stringify(res.data.data));
        updateUserDetails(res.data?.data);
      })
      .finally(()=> setLoading(false));
  };

  useEffect(() => {
    if (!user?.id) {
      toast.success("Please Login First");
    }
  }, [user]);

  return (
    <div className="p-6 sm:p-14 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* 1. TOP HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Account Settings
          </h1>
          <p className="text-lg text-slate-500 font-medium ">
            Manage your identity and contact preferences.
          </p>
        </div>
      </div>

      {/* 2. THE BENTO GRID */}
      <div className="grid grid-cols-12 gap-4">
        {/* LEFT CARD: Detailed Form (Span 8) */}
        <div className="col-span-12 lg:col-span-8 grid grid-cols-2 gap-6 bg-white border border-slate-200 rounded-[16px] p-4 sm:p-8 md:p-10 shadow-sm">
          {/* Input Pairs */}
          <div className="col-span-2 md:col-span-1 space-y-2">
            <label className="text-sm font-medium text-[#333333] ml-1">
              First Name
            </label>
            <input
              type="text"
              value={userData?.firstName}
              onChange={(e) =>
                setUserData({ ...userData, ["firstName"]: e.target.value })
              }
              placeholder="Calvin"
              disabled={!isEditing}
              className="w-full bg-slate-50 border-none rounded-2xl px-5 py-4 focus:ring-2 focus:ring-blue-500/20 transition-all text-slate-700"
            />
          </div>

          <div className="col-span-2 md:col-span-1 space-y-2">
            <label className="text-sm font-medium text-[#333333] ml-1">
              Last Name
            </label>
            <input
              type="text"
              value={userData?.lastName}
              onChange={(e) =>
                setUserData({ ...userData, ["lastName"]: e.target.value })
              }
              placeholder="Carlo"
              disabled={!isEditing}
              className="w-full bg-slate-50 border-none rounded-2xl px-5 py-4 focus:ring-2 focus:ring-blue-500/20 transition-all text-slate-700"
            />
          </div>

          <div className="col-span-2 space-y-2">
            <label className="text-sm font-medium text-[#333333] ml-1">
              Email Address
            </label>
            <div className="relative">
              <Mail
                className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                type="email"
                value={userData?.email}
                onChange={(e) =>
                  setUserData({ ...userData, ["email"]: e.target.value })
                }
                placeholder="hello@calvincarlo.ca"
                disabled={!isEditing}
                className="w-full bg-slate-50 border-none rounded-2xl pl-14 pr-5 py-4 focus:ring-2 focus:ring-blue-500/20 transition-all text-slate-700"
              />
            </div>
          </div>

          <div className="col-span-2 md:col-span-1 space-y-2">
            <label className="text-sm font-medium text-[#333333] ml-1">
              Phone
            </label>
            <div className="relative">
              <Phone
                className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                type="tel"
                value={userData?.phone}
                onChange={(e) =>
                  setUserData({ ...userData, ["phone"]: e.target.value })
                }
                placeholder="+1 (416) 000-0000"
                disabled={!isEditing}
                className="w-full bg-slate-50 border-none rounded-2xl pl-14 pr-5 py-4 focus:ring-2 focus:ring-blue-500/20 transition-all text-slate-700"
              />
            </div>
          </div>

          <div className="col-span-2 space-y-2">
            {isEditing ? (
              <div className="space-x-4">
                <button
                  className="px-6 py-3 rounded-2xl bg-white border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-all text-sm"
                  onClick={() => setIsEditing(false)}
                >
                  Discard
                </button>
                <button
                  className="px-8 py-3 rounded-2xl bg-blue-600 text-white font-semibold shadow-sm shadow-blue-200 hover:bg-blue-700 transition-all text-sm"
                  disabled={loading}
                  onClick={() => updateUserProfile()}
                >
                  Save
                </button>
              </div>
            ) : (
              <button
                className="px-8 py-3 rounded-xl bg-blue-600 text-white font-semibold shadow-sm shadow-blue-200 hover:bg-blue-700 transition-all text-sm"
                onClick={() => setIsEditing(true)}
              >
                Update
              </button>
            )}
          </div>
        </div>

        {/* RIGHT CARD: Profile Identity (Span 4) */}
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-6">
          <div className="bg-slate-900 rounded-[16px] p-8 text-white relative overflow-hidden shadow-xl">
            <div className="relative z-10">
              <h2 className="text-2xl font-bold">Calvin Carlo</h2>
              <p className="text-slate-400 text-sm mt-1 flex items-center gap-2">
                <MapPin size={14} /> Toronto, ON
              </p>
              <div className="mt-8 pt-8 border-t border-white/10 flex flex-col gap-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Member Since</span>
                  <span className="font-semibold text-slate-200">Jan 2026</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Last Booking</span>
                  <span className="font-semibold text-slate-200">
                    2 days ago
                  </span>
                </div>
              </div>
            </div>
            {/* Decorative Background Glow */}
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-blue-600/20 blur-[100px]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
